import type { TransactionEvent } from '@sentry/types'

/**
 * Mapping of URL path patterns to microservice names.
 * Derived from the Terraform infrastructure configuration.
 *
 * If no pattern matches a request, it defaults to 'oracle'.
 *
 * To update, refer to the Terraform config for the latest microservice endpoint definitions.
 */
export const microserviceMap: { pattern: string; service: string }[] = [
  { pattern: '/v1/acre/acctmgmt/', service: 'acre-client-creator-service' },
  { pattern: '/v1/user/', service: 'be-auth-service' },
  { pattern: '/v1/client/logout', service: 'be-auth-service' },
  { pattern: '/v1/acre/search/', service: 'search-service' },
  { pattern: '/v2/acre/search/', service: 'search-v2-service' },
  { pattern: '/v1/acre/transcriptor/', service: 'transcriptor-service' },
  { pattern: '/v2/acre/general-insurance/', service: 'general-insurance-v2-service' },
  { pattern: '/v1/acre/lead-generation/', service: 'lead-generation-service' },
  { pattern: '/v1/acre/protection/', service: 'protection-service' },
  { pattern: '/v2/acre/protection/', service: 'protection-v2-service' },
  { pattern: '/v1/acre/local-sourcing/', service: 'sourcing-service' },
  { pattern: '/v1/acre/companies-house/', service: 'companies-house-service' },
  { pattern: '/v1/acre/checks-service/', service: 'checks-service' },
  { pattern: '/v1/checks-service/', service: 'checks-service' },
  { pattern: '/v1/acre/classification/', service: 'document-classification-service' },
  { pattern: '/v1/acre/mi/', service: 'mi-query-service' },
  { pattern: '/v1/mi/', service: 'mi-query-service' },
  { pattern: '/v1/acre/propertydata/', service: 'property-data-service' },
  { pattern: '/v1/propertydata/', service: 'property-data-service' },
  { pattern: '/geoserver/', service: 'geoserver-service' },
  { pattern: '/v1/acre/intelliflo/', service: 'intelliflo-sync-service' },
  { pattern: '/v1/acre/accounting/', service: 'accounting-service' },
  { pattern: '/v1/accounting/', service: 'accounting-service' },
  { pattern: '/v1/acre/authentication/', service: 'authentication-service' },
  { pattern: '/v1/authentication/', service: 'authentication-service' },
  { pattern: '/v1/acre/communications/', service: 'communications-service' },
  { pattern: '/v1/communications/', service: 'communications-service' },
  { pattern: '/v1/acre/notifications/', service: 'notifications-service' },
  { pattern: '/v1/notifications/', service: 'notifications-service' },
  { pattern: '/v1/acre/mortgage-application/', service: 'mortgage-application-service' },
  { pattern: '/v1/acre/business/', service: 'business-tools-service' },
  { pattern: '/v1/acre/internal-tools/', service: 'internal-tools-service' },
  { pattern: '/v1/acre/data-migrations/', service: 'data-migrations-service' },
]

/**
 * Sentry beforeSendTransaction callback that enriches auto-generated HTTP spans
 * with an http.microservice tag derived from the microserviceMap.
 */
export const enrichHttpSpansWithMicroserviceTags = (event: TransactionEvent): TransactionEvent => {
  event.spans?.forEach((span) => {
    if (span.op !== 'http.client' || typeof span.description !== 'string') return

    const [url] = span.description.split(' ', 2)
    if (!url) return

    const matched = microserviceMap.find(({ pattern }) => url.includes(pattern))
    const microservice = matched?.service ?? 'oracle'

    span.tags = {
      ...span.tags,
      'http.microservice': microservice,
    }
  })

  return event
}
